import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import supabase from '../supabase';
import SafeIcon from '../common/SafeIcon';
import { FiArrowLeft, FiLoader, FiAlertCircle } from 'react-icons/fi';

const NewsletterDetail = () => {
  const { slug } = useParams();
  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    const fetchNewsletter = async () => {
      try {
        const { data, error: dbError } = await supabase
          .from('newsletters_20240320')
          .select('*')
          .eq('slug', slug)
          .single();

        if (dbError) throw dbError;
        setNewsletter(data);
      } catch (err) {
        console.error('Error fetching newsletter:', err);
        setError('Newsletter not found');
      } finally {
        setLoading(false);
      }
    };

    fetchNewsletter();
  }, [slug]);

  const updateIframeHeight = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      try {
        const height = iframe.contentWindow.document.body.scrollHeight;
        iframe.style.height = `${height}px`;
      } catch (err) {
        console.error('Could not auto-size iframe:', err);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', updateIframeHeight);
    return () => window.removeEventListener('resize', updateIframeHeight);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-stone-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-2xl animate-spin mx-auto mb-6"></div>
          <p className="text-stone-500 font-black uppercase tracking-widest text-xs animate-pulse">Opening Issue...</p>
        </div>
      </div>
    );
  }

  if (error || !newsletter) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4 bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiAlertCircle} className="text-5xl text-stone-300 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-stone-900 uppercase tracking-widest mb-4">Issue Not Found</h2>
          <Link to="/newsletters" className="text-emerald-600 font-bold uppercase tracking-widest text-xs hover:underline">
            Back to Archive
          </Link>
        </div>
      </div>
    );
  }

  // Inject <base target="_blank"> to ensure links open in new tabs
  const processedHtml = `
    <head>
      <base target="_blank">
      <style>
        body { margin: 0; padding: 0; }
        img { max-width: 100%; height: auto; }
      </style>
    </head>
    ${newsletter.body_html}
  `;

  return (
    <div className="min-h-screen bg-stone-50 pt-32 pb-20 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <Link to="/newsletters" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-600 transition-colors font-black uppercase tracking-widest text-[10px]">
            <SafeIcon icon={FiArrowLeft} /> Back to all issues
          </Link>
        </div>

        <header className="mb-12">
          <div className="text-stone-400 font-black uppercase tracking-widest text-[10px] mb-2">
            {new Date(newsletter.created_at).toLocaleDateString(undefined, { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-stone-900 uppercase tracking-tighter">
            {newsletter.subject}
          </h1>
        </header>

        <div className="bg-white rounded-[32px] overflow-hidden border border-stone-200 shadow-sm">
          <iframe
            ref={iframeRef}
            srcDoc={processedHtml}
            className="w-full border-none overflow-hidden"
            onLoad={updateIframeHeight}
            title={newsletter.subject}
            scrolling="no"
          />
        </div>
      </div>
    </div>
  );
};

export default NewsletterDetail;